import { useEffect, useState } from 'react'
import { Popover, MenuItem, Button, Field } from '../ds/index.js'

// Shared "⋯" column/stage menu for board views — consolidates the three
// copy-paste local menus this replaces: `ColumnMenu` (BoardView),
// `PipelineStageMenu` (DealsBoardView), `StageMenu` (EngagementsView).
//
// Base behavior (all three menus): rename via inline field + save, and a
// gated delete action. Two of the three menus (Deals/Engagements) additionally
// gate delete on `canDeleteStage`-style capacity checks — that's the `canDelete`
// prop here. All three also offer an identical recolor swatch and left/right
// reorder pair; those aren't unique to one board, but to avoid silently
// dropping them they're exposed as optional prop pairs (`onRecolor`/
// `recolorValue`, `onMoveLeft`/`onMoveRight`/`canMoveLeft`/`canMoveRight`) that
// render nothing unless a board opts in when it migrates (Tasks 13-15).
//
// Note: the original menus had a "confirm delete" sub-step before calling
// delete; this component implements that internally (see `mode ===
// 'confirm-delete'` below) so all three boards inherit it uniformly.
export default function BoardColumnMenu({
  open,
  onToggle,
  onClose,
  canDelete = true,
  onRename,
  onDelete,
  renameLabel = 'Rename',
  deleteLabel = 'Delete',
  renameValue = '',
  // Display name used only in the delete-confirm copy ("Delete {name}?").
  name,
  // Optional extra reassurance line shown under the confirm copy.
  confirmNote = '',
  // Optional recolor slot — union action present in all three source menus.
  onRecolor,
  recolorValue,
  // Optional reorder slot — union action present in all three source menus.
  onMoveLeft,
  onMoveRight,
  canMoveLeft = true,
  canMoveRight = true,
}) {
  const [nameVal, setNameVal] = useState(renameValue)
  const [mode, setMode] = useState(null) // null | 'confirm-delete'

  // Re-seed the draft each time the menu opens (or the underlying value
  // changes), mirroring the source menus' "reset on open" behavior.
  useEffect(() => {
    if (open) { setNameVal(renameValue); setMode(null) }
  }, [open, renameValue])

  const displayName = name || renameValue || 'this column'

  const submitRename = () => {
    const trimmed = nameVal.trim()
    if (trimmed && onRename) onRename(trimmed)
    onClose()
  }

  return (
    <div className="cn-popover-wrap">
      <Button variant="subtle" size="sm" onClick={onToggle} title="Column options">⋯</Button>
      <Popover open={open} onClose={onClose}>
        {mode === 'confirm-delete' ? (
          <div style={{ padding: '2px 2px 6px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-body)', lineHeight: 1.5, marginBottom: 10 }}>
              Delete <strong style={{ color: 'var(--color-white)' }}>{displayName}</strong>?
            </div>
            {confirmNote && (
              <div style={{ fontSize: 11, color: 'var(--color-muted)', lineHeight: 1.4, marginBottom: 10 }}>
                {confirmNote}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <Button variant="ghost" size="sm" onClick={() => setMode(null)} style={{ flex: 1, justifyContent: 'center' }}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={() => { onDelete(); onClose() }} style={{ flex: 1, justifyContent: 'center' }}>
                Delete
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: '2px 2px 6px' }}>
              <Field
                label={renameLabel}
                value={nameVal}
                onChange={(e) => setNameVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitRename()
                  if (e.key === 'Escape') { setNameVal(renameValue); onClose() }
                }}
                autoFocus
              />
              <Button variant="gold" size="sm" onClick={submitRename} style={{ width: '100%', justifyContent: 'center', marginTop: 6 }}>
                Save
              </Button>
            </div>

            {onRecolor && (
              <div style={{ padding: '2px 2px 6px' }}>
                <span className="cn-field-label">Color</span>
                <input
                  type="color"
                  defaultValue={recolorValue}
                  onChange={(e) => onRecolor(e.target.value)}
                  style={{ width: '100%', height: 32, background: 'none', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', padding: 2 }}
                />
              </div>
            )}

            {(onMoveLeft || onMoveRight) && (
              <>
                {onMoveLeft && <MenuItem onClick={onMoveLeft} disabled={!canMoveLeft}>← Move left</MenuItem>}
                {onMoveRight && <MenuItem onClick={onMoveRight} disabled={!canMoveRight}>→ Move right</MenuItem>}
              </>
            )}

            {canDelete && (
              <MenuItem danger onClick={() => setMode('confirm-delete')}>{deleteLabel}</MenuItem>
            )}
          </>
        )}
      </Popover>
    </div>
  )
}
